/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createSlice } from '@reduxjs/toolkit';

import { Role } from '../../../../ts';
import { FeatureFlag } from '../../../../ts/feature-flags';
import { AccessControlSlice } from '../../../../ts/state';
import { features } from '../../util/config';
import { listAssistants } from '../assistants/actions';
import { listEnvironmentsWithArgs } from '../environments/actions';
import { getInstance, updateInstance } from '../instance/actions';
import { listSkills } from '../skills/actions';

const formatRole = (role?: Role): undefined | Role =>
  role === Role.UNAVAILABLE ? undefined : role;

// This action is to be used by developers when testing access control
// using the Redux DevTools. It is not used anywhere else in the code.
const SET_ROLE_ACTION = 'access-control-set-role';

// Initial State
export const initialState: AccessControlSlice = Object.freeze({
  accessControlStatus: Boolean(features?.[FeatureFlag.ACCESS_CONTROL] || false),
  instance: undefined,
  assistants: {},
  environments: {},
  skills: {}
});

// Slice + Reducer
const slice = createSlice({
  name: 'accessControl',
  initialState,
  reducers: {
    [SET_ROLE_ACTION]: (state, action) => {
      const { accessControlStatus, instance, assistant, skill } =
        action.payload;

      if (accessControlStatus) {
        state.accessControlStatus = accessControlStatus;
      }
      if (instance) {
        state.instance = instance;
      }
      if (assistant) {
        state.assistants[assistant.assistant_id] = assistant.role;
      }
      if (skill) {
        state.skills[skill.skill_id] = skill.role;
      }
    }
  },
  extraReducers: builder => {
    // List assistants.
    builder.addCase(listAssistants.fulfilled, (state, action) => {
      const assistants = action.payload || [];
      assistants.forEach(assistant => {
        state.assistants[assistant.assistant_id] = formatRole(assistant.role);
      });
    });

    // List environments
    builder.addCase(listEnvironmentsWithArgs.fulfilled, (state, action) => {
      const environments = action.payload || [];
      environments.forEach(environment => {
        state.environments[environment.environment_id] = formatRole(
          environment.role
        );
      });
    });

    // Load skills.
    builder.addCase(listSkills.fulfilled, (state, action) => {
      const skills = action.payload || [];
      skills.forEach(skill => {
        state.skills[skill.skill_id] = formatRole(skill.role);
      });
    });

    // Fetch instance.
    builder.addCase(getInstance.fulfilled, (state, action) => {
      const { instance_role, featureFlags = {} } = action.payload;
      state.instance = formatRole(instance_role);
      if (featureFlags[FeatureFlag.ACCESS_CONTROL]) {
        state.accessControlStatus = Boolean(
          featureFlags[FeatureFlag.ACCESS_CONTROL]
        );
      }
    });

    // Update instance.
    builder.addCase(updateInstance.fulfilled, (state, action) => {
      const { instance_role, featureFlags = {} } = action.payload;
      state.instance = formatRole(instance_role);
      if (featureFlags[FeatureFlag.ACCESS_CONTROL]) {
        state.accessControlStatus = Boolean(
          featureFlags[FeatureFlag.ACCESS_CONTROL]
        );
      }
    });
  }
});

export default slice.reducer;
